import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { commissionsService } from '../../../services/commissionsService';

type CommissionStatus = 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'CANCELLED' | 'DISPUTED';

interface Commission {
  id: string;
  patronId: string;
  patronName?: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  status: CommissionStatus;
  deadline?: string;
  createdAt: string;
  acceptedAt?: string;
  completedAt?: string;
}

interface Stats {
  total: number;
  pending: number;
  active: number;
  completed: number;
  totalEarnings: number;
}

const CommissionsDashboardPage: React.FC = () => {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    pending: 0,
    active: 0,
    completed: 0,
    totalEarnings: 0,
  });
  const [filter, setFilter] = useState<'all' | CommissionStatus>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, [filter]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await commissionsService.getCreatorCommissions(filter === 'all' ? undefined : filter);
      setCommissions(data.commissions || []);
      setStats(data.stats || stats);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: CommissionStatus) => {
    const colors: Record<CommissionStatus, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      ACCEPTED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-purple-100 text-purple-800',
      DELIVERED: 'bg-indigo-100 text-indigo-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      DISPUTED: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Commissions</h1>
        <p className="mt-2 text-gray-600">Manage custom work requests from your patrons</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Active</p>
          <p className="text-2xl font-bold text-purple-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Earnings</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(stats.totalEarnings, 'USD')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { key: 'all', label: 'All' },
              { key: 'PENDING', label: 'Pending' },
              { key: 'ACCEPTED', label: 'Accepted' },
              { key: 'IN_PROGRESS', label: 'In Progress' },
              { key: 'DELIVERED', label: 'Delivered' },
              { key: 'COMPLETED', label: 'Completed' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key as any)}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : commissions.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No commissions</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filter === 'all'
              ? "You haven't received any commission requests yet."
              : `No ${filter.toLowerCase().replace('_', ' ')} commissions.`}
          </p>
        </div>
      ) : (
        /* Commissions List */
        <div className="space-y-4">
          {commissions.map((commission) => (
            <Link
              key={commission.id}
              to={`/dashboard/commissions/${commission.id}`}
              className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{commission.title}</h3>
                      {getStatusBadge(commission.status)}
                    </div>
                    <p className="text-gray-600 mb-3 line-clamp-2">{commission.description}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <span>From: {commission.patronName || 'Anonymous'}</span>
                      <span>Requested: {formatDate(commission.createdAt)}</span>
                      {commission.deadline && (
                        <span>Deadline: {formatDate(commission.deadline)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(commission.amount, commission.currency)}
                    </p>
                    {commission.status === 'PENDING' && (
                      <p className="text-sm text-yellow-600 mt-1">Awaiting response</p>
                    )}
                    {commission.status === 'DELIVERED' && (
                      <p className="text-sm text-indigo-600 mt-1">Pending approval</p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommissionsDashboardPage;
